import type {
  ButtonHTMLAttributes,
  ComponentType,
  FocusEvent,
  MouseEvent,
  PropsWithChildren
} from 'react';
import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import type { ImageValue } from '@elfsight/configurator-contracts';
import { Icon } from '@elfsight/lib-icon';
import type { FourValueTupleConvertable } from '@elfsight/widget-utils';
import {
  createReadableClass,
  getLineHeight,
  roundTo4,
  to4VTuple
} from '@elfsight/widget-utils';
import { clamp } from 'es-toolkit';
import {
  getVariantStyles,
  type StyleVariants
} from '../utils/get-variant-styles';
import { COLOR_BLACK, COLOR_TRANSPARENT } from './constants';

type Variants = 'initial' | 'hover' | 'focus' | 'tap';
type ButtonVariants = StyleVariants<Variants>;

export type ButtonBaseProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'className'
  | 'style'
  | 'disabled'
  | 'type'
  | 'role'
  | 'aria-label'
  | 'tabIndex'
  | 'onClick'
  | 'onBlur'
  | 'onTouchStart'
  | 'onFocus'
  | 'onKeyDown'
  | 'onMouseLeave'
  | 'onMouseOver'
  | 'onMouseDown'
  | 'onMouseEnter'
  | 'onMouseUp'
> &
  PropsWithChildren<{
    lineHeight?: number;
    fontSize?: number;
    fontWeight?: string | number;
    fontUppercase?: boolean;
    fontColor?: string;
    fontFamily?: string;
    fontStyle?: 'normal' | 'italic';
    backgroundColor?: string;
    iconLeft?: ComponentType | ImageValue;
    iconRight?: ComponentType | ImageValue;
    iconOnly?: boolean;
    width?: number | string;
    height?: number | string;
    borderWidth?: number;
    borderColor?: string;
    borderRadius?: FourValueTupleConvertable<number | string>;
    padding?: FourValueTupleConvertable<number>;
    variants?: ButtonVariants;
    overlayVariants?: ButtonVariants;
    disableEllipsis?: boolean;
    iconSize?: number;
  }>;

export const ButtonBase = forwardRef<HTMLButtonElement, ButtonBaseProps>(
  (
    {
      children,
      iconLeft,
      iconRight,
      iconOnly = false,
      lineHeight: derivedLineHeight,
      backgroundColor = COLOR_TRANSPARENT,
      fontSize = 14,
      fontWeight,
      fontUppercase = false,
      fontColor = COLOR_BLACK,
      fontFamily = 'inherit',
      fontStyle,
      width: derivedWidth,
      height: derivedHeight,
      borderWidth = 1,
      borderColor = COLOR_TRANSPARENT,
      borderRadius = 4,
      padding: derivedPadding,
      disabled,
      variants = {} as ButtonVariants,
      overlayVariants = {} as ButtonVariants,
      style,
      type = 'button',
      disableEllipsis = false,
      iconSize = roundTo4(fontSize) + 4,
      onClick,
      onFocus,
      onBlur,
      onMouseEnter,
      onMouseLeave,
      onMouseDown,
      onMouseUp,
      ...forwardingProps
    },
    forwardingRef
  ) => {
    const [hover, setHover] = useState(false);
    const [tap, setTap] = useState(false);
    const [focus, setFocus] = useState(false);

    // order is important
    const appliedVariants = [
      'initial',
      hover && 'hover',
      tap && 'tap',
      focus && 'focus',
      disabled && 'disabled',
      !tap && focus && 'focusVisible'
    ].filter(Boolean) as Variants[];

    const padding =
      derivedPadding ||
      (iconOnly ? fontSize / 1.5 : [fontSize / 1.5, fontSize * 1.5]);
    const paddings = Array.isArray(padding)
      ? padding.map((p: number) => Math.max(p - borderWidth, 0))
      : padding;

    const [paddingTop, paddingRight, paddingBottom, paddingLeft] = to4VTuple(
      paddings as FourValueTupleConvertable<number>
    );

    const lineHeight = derivedLineHeight
      ? derivedLineHeight
      : getLineHeight({ fontSize });

    const height = (() => {
      if (!derivedHeight) {
        return undefined;
      }

      const numberHeight =
        typeof derivedHeight === 'string'
          ? parseInt(derivedHeight)
          : derivedHeight;
      const lineHeightValue = parseFloat(lineHeight.toString());

      return clamp(
        numberHeight,
        lineHeightValue +
          (paddingTop && paddingBottom ? paddingTop + paddingBottom : 0),
        numberHeight
      );
    })();

    const [
      borderTopLeftRadius,
      borderTopRightRadius,
      borderBottomRightRadius,
      borderBottomLeftRadius
    ] = to4VTuple(borderRadius);

    const iconRender = (
      position: 'left' | 'right',
      component?: ComponentType | ImageValue
    ) => {
      if (!component) {
        return null;
      }

      let IconComponent = position === 'left' ? LeftIcon : RightIcon;

      if (iconOnly) {
        IconComponent = ButtonIcon;
      }

      return (
        <IconComponent
          src={typeof component === 'object' ? component.url : undefined}
          component={typeof component !== 'object' ? component : undefined}
          size={iconSize}
          fill="currentColor"
          alt=""
        />
      );
    };

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (event.detail > 0) {
        event.currentTarget.blur();
      }
      if (onClick && !disabled) {
        onClick(event);
      }
    };

    const handleFocus = (event: FocusEvent<HTMLButtonElement>) => {
      setFocus(true);
      onFocus?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLButtonElement>) => {
      setFocus(false);
      onBlur?.(event);
    };

    const handleMouseEnter = (event: MouseEvent<HTMLButtonElement>) => {
      setHover(true);
      onMouseEnter?.(event);
    };

    const handleMouseLeave = (event: MouseEvent<HTMLButtonElement>) => {
      setHover(false);
      setTap(false);
      onMouseLeave?.(event);
    };

    const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
      setTap(true);
      onMouseDown?.(event);
    };

    const handleMouseUp = (event: MouseEvent<HTMLButtonElement>) => {
      setTap(false);
      onMouseUp?.(event);
    };

    const fixOverlayBorderRadius = (borderRadius: string | number) => {
      const borderRadiusWithUnit =
        typeof borderRadius === 'number' ||
        String(parseInt(borderRadius)) === borderRadius
          ? `${borderRadius}px`
          : borderRadius;
      return `calc(${borderRadiusWithUnit} - ${borderWidth}px)`;
    };

    return (
      <ButtonContainer
        {...forwardingProps}
        ref={forwardingRef}
        type={type}
        style={{
          borderTopLeftRadius,
          borderTopRightRadius,
          borderBottomRightRadius,
          borderBottomLeftRadius,
          borderColor,
          width: derivedWidth,
          height,
          lineHeight: iconOnly ? 'normal' : lineHeight,
          color: fontColor,
          padding: 0,
          fontFamily,
          fontWeight,
          fontSize: iconOnly ? 0 : fontSize,
          fontStyle,
          backgroundColor,
          textTransform: fontUppercase ? 'uppercase' : 'none',
          borderWidth,
          ...getVariantStyles(variants, appliedVariants),
          ...style
        }}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick}
      >
        <Overlay
          style={{
            paddingTop,
            paddingRight,
            paddingBottom,
            paddingLeft,
            borderTopLeftRadius: fixOverlayBorderRadius(borderTopLeftRadius),
            borderTopRightRadius: fixOverlayBorderRadius(borderTopRightRadius),
            borderBottomRightRadius: fixOverlayBorderRadius(
              borderBottomRightRadius
            ),
            borderBottomLeftRadius: fixOverlayBorderRadius(
              borderBottomLeftRadius
            ),
            ...getVariantStyles(overlayVariants, appliedVariants)
          }}
        >
          {iconRender('left', iconLeft)}
          {disableEllipsis ? (
            <span>{children}</span>
          ) : (
            <Ellipsis>{children}</Ellipsis>
          )}
          {iconRender('right', iconRight)}
        </Overlay>
      </ButtonContainer>
    );
  }
);

const ButtonIcon = styled(Icon).attrs({
  className: createReadableClass({
    component: 'button-base',
    element: 'icon'
  })
})``;
const LeftIcon = styled(ButtonIcon).attrs({
  className: createReadableClass({
    component: 'button-base',
    element: 'left-icon'
  })
})`
  margin-right: 8px;
  margin-left: -4px;
`;
const RightIcon = styled(ButtonIcon).attrs({
  className: createReadableClass({
    component: 'button-base',
    element: 'right-icon'
  })
})`
  margin-left: 8px;
  margin-right: -4px;
`;

const ButtonContainer = styled.button.attrs({
  className: createReadableClass({
    component: 'button-base',
    element: 'container'
  })
})`
  ${({ disabled, onClick }) => css`
    position: relative;
    height: fit-content;
    width: fit-content;
    max-width: 100%;
    cursor: ${disabled ? 'not-allowed' : !onClick ? 'default' : 'pointer'};
    ${disabled &&
    css`
      opacity: 0.3;
      pointer-events: none;
    `}
    box-sizing: border-box;
    background: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    border-style: solid;
    transition-property: opacity, transform, color, background-color;
    transition-duration: 0.1s;
    transition-timing-function: ease-in;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

const Overlay = styled.span.attrs({
  className: createReadableClass({
    component: 'button-base',
    element: 'overlay'
  })
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  transition-property: opacity, transform, color, background-color;
  transition-duration: 0.1s;
  transition-timing-function: ease-in;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Ellipsis = styled.span.attrs({
  className: createReadableClass({
    component: 'button-base',
    element: 'ellipsis'
  })
})`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
